import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Home, DollarSign, Edit, Plus, Building2 } from 'lucide-react';
import api from '../../lib/api';

const PropertyManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showRentDialog, setShowRentDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  const [propertyForm, setPropertyForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'BS',
    postal_code: '',
    property_type: 'residential'
  });

  const [unitForm, setUnitForm] = useState({
    unit_number: '',
    bedrooms: 0,
    bathrooms: 0,
    square_feet: 0,
    rent_amount: 0,
    status: 'vacant'
  });

  const [rentForm, setRentForm] = useState({
    rent_amount: 0
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/landlord/properties-units');
      setProperties(response.data.properties || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
      setLoading(false);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      await api.post('/landlord/properties', propertyForm);
      toast.success('Property created successfully');
      setShowPropertyDialog(false);
      setPropertyForm({
        name: '',
        address: '',
        city: '',
        state: '',
        country: 'BS',
        postal_code: '',
        property_type: 'residential'
      });
      fetchProperties();
    } catch (error) {
      console.error('Error creating property:', error);
      toast.error('Failed to create property');
    }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/landlord/properties/${selectedProperty.id}/units`, unitForm);
      toast.success('Unit created successfully');
      setShowUnitDialog(false);
      setUnitForm({
        unit_number: '',
        bedrooms: 0,
        bathrooms: 0,
        square_feet: 0,
        rent_amount: 0,
        status: 'vacant'
      });
      fetchProperties();
    } catch (error) {
      console.error('Error creating unit:', error);
      toast.error('Failed to create unit');
    }
  };

  const handleUpdateRent = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/landlord/units/${selectedUnit.id}/rent`, rentForm);
      toast.success('Rent price updated successfully');
      setShowRentDialog(false);
      fetchProperties();
    } catch (error) {
      console.error('Error updating rent:', error);
      toast.error('Failed to update rent');
    }
  };

  const openRentDialog = (unit) => {
    setSelectedUnit(unit);
    setRentForm({ rent_amount: unit.rent_amount || 0 });
    setShowRentDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Properties & Units</h2>
          <p className="text-gray-600 mt-1">Manage your properties and set rental prices</p>
        </div>
        <Button 
          onClick={() => setShowPropertyDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Property
        </Button>
      </div>

      {properties.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No properties yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first property</p>
            <Button onClick={() => setShowPropertyDialog(true)}>
              Add Property
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {properties.map((property) => (
            <Card key={property.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Home className="h-5 w-5 mr-2 text-emerald-600" />
                    {property.name}
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSelectedProperty(property);
                      setShowUnitDialog(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Unit
                  </Button>
                </div>
                <p className="text-sm text-gray-600">{property.address}, {property.city}</p>
              </CardHeader>
              <CardContent>
                {!property.units || property.units.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No units added yet
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {property.units.map((unit) => (
                      <div key={unit.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Unit {unit.unit_number}</span>
                          <Badge className={unit.status === 'occupied' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                            {unit.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          {unit.bedrooms} bed • {unit.bathrooms} bath
                          {unit.square_feet > 0 && ` • ${unit.square_feet} sqft`}
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div>
                            <div className="text-xs text-gray-500">Rent</div>
                            <div className="text-lg font-bold text-emerald-600">
                              ${unit.rent_amount ? unit.rent_amount.toFixed(2) : '0.00'}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openRentDialog(unit)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Property Dialog */}
      <Dialog open={showPropertyDialog} onOpenChange={setShowPropertyDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Property</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProperty} className="space-y-4">
            <div>
              <Label>Property Name *</Label>
              <Input
                required
                placeholder="e.g., Sunset Apartments"
                value={propertyForm.name}
                onChange={(e) => setPropertyForm({...propertyForm, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                required
                placeholder="Street address"
                value={propertyForm.address}
                onChange={(e) => setPropertyForm({...propertyForm, address: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input
                  required
                  value={propertyForm.city}
                  onChange={(e) => setPropertyForm({...propertyForm, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State/Province</Label>
                <Input
                  value={propertyForm.state}
                  onChange={(e) => setPropertyForm({...propertyForm, state: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country *</Label>
                <Input
                  required
                  value={propertyForm.country}
                  onChange={(e) => setPropertyForm({...propertyForm, country: e.target.value})}
                />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={propertyForm.postal_code}
                  onChange={(e) => setPropertyForm({...propertyForm, postal_code: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPropertyDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Create Property
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Unit Dialog */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Unit to {selectedProperty?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUnit} className="space-y-4">
            <div>
              <Label>Unit Number *</Label>
              <Input
                required
                placeholder="e.g., 101"
                value={unitForm.unit_number}
                onChange={(e) => setUnitForm({...unitForm, unit_number: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Bedrooms</Label>
                <Input
                  type="number"
                  min="0"
                  value={unitForm.bedrooms}
                  onChange={(e) => setUnitForm({...unitForm, bedrooms: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Bathrooms</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={unitForm.bathrooms}
                  onChange={(e) => setUnitForm({...unitForm, bathrooms: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Sq Ft</Label>
                <Input
                  type="number"
                  min="0"
                  value={unitForm.square_feet}
                  onChange={(e) => setUnitForm({...unitForm, square_feet: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <Label>Monthly Rent ($) *</Label>
              <Input
                required
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 1500"
                value={unitForm.rent_amount}
                onChange={(e) => setUnitForm({...unitForm, rent_amount: parseFloat(e.target.value)})}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUnitDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Create Unit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Rent Dialog */}
      <Dialog open={showRentDialog} onOpenChange={setShowRentDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update Rent for Unit {selectedUnit?.unit_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRent} className="space-y-4">
            <div>
              <Label>Monthly Rent ($) *</Label>
              <Input
                required
                type="number"
                min="0"
                step="0.01"
                value={rentForm.rent_amount}
                onChange={(e) => setRentForm({rent_amount: parseFloat(e.target.value)})}
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: ${selectedUnit?.rent_amount?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowRentDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Update Rent
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyManagement;
